import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from './use-agent';
import { Button } from '@/ui/Button';
import { Field, INPUT_CLASS } from '@/ui/Field';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { PROXY_AUTH } from '@/api/config';

export function SignInPage() {
  const { session, signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [key, setKey] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (session.status === 'signed-in') {
    return <Navigate to="/" replace />;
  }

  if (PROXY_AUTH) {
    return (
      <main className="mx-auto mt-24 max-w-sm px-4">
        <h1 className="text-xl font-semibold">HiSpin support console</h1>
        <p className="mt-6 text-sm text-slate-600">
          Your sign-in is not linked to a support agent. Ask an admin to add you, then reload.
        </p>
      </main>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(key.trim());
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from && from !== '/sign-in' ? from : '/', { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto mt-24 max-w-sm px-4">
      <h1 className="text-xl font-semibold">HiSpin support console</h1>
      <form
        onSubmit={submit}
        className="mt-6 space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
        autoComplete="off"
      >
        <Field id="key" label="Your access key" hint="Kept in this tab only. Close the tab to forget it.">
          <input
            id="key"
            type="password"
            required
            autoFocus
            className={INPUT_CLASS}
            placeholder="Personal agent key"
            value={key}
            onChange={(event) => setKey(event.target.value)}
          />
        </Field>
        <ErrorNotice error={error} />
        <Button type="submit" disabled={busy || !key.trim()} className="w-full">
          {busy ? 'Checking…' : 'Sign in'}
        </Button>
      </form>
    </main>
  );
}
